import './RoleStageContent.scss';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  NavLink,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import Loader from 'components/atoms/Loader/Loader';
import { HelmetAndPageAnnouncer } from 'components/organisms/HelmetAndPageAnnouncer/HelmetAndPageAnnouncer';
import { getRoleHomeUrl } from 'constants/routes';
import {
  ContentStageCandidateStatus,
  useContentStageCandidateMarkCompleteMutation,
  useContentStageCandidateQuery,
} from 'generated/graphql';
import PortalFooter from '../CandidatePortal/PortalLayout/PortalFooter';
import PortalNavbar from '../CandidatePortal/PortalLayout/PortalNavbar';
import { getPortalBrandingStyle } from '../CandidatePortal/CandidatePortal.helpers';
import ContentBlock from './ContentBlock';

const RoleStageContent: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { handleMsgType } = useNotification();
  const { roleId, stageId } = useParams();

  const { data, loading } = useContentStageCandidateQuery({
    variables: {
      roleId: roleId as string,
      stageId: stageId as string,
    },
    skip: !roleId || !stageId,
    onError: error => {
      handleMsgType({
        type: TNotification.error,
        message: error?.message,
      });
    },
    fetchPolicy: 'network-only',
  });

  const role = data?.myRole;
  const stage = role?.contentStages[0];

  const backUrl = getRoleHomeUrl(
    roleId as string,
    role?.candidatePortalEnabled
  );

  const [markComplete, { loading: isCompleting }] =
    useContentStageCandidateMarkCompleteMutation({
      onCompleted: () => {
        navigate(backUrl);
      },
      onError: error => {
        handleMsgType({
          type: TNotification.error,
          message: error?.message,
        });
      },
    });

  // Nothing left to do on an already-completed stage — back to the portal.
  const isAlreadyCompleted =
    !!stage && stage.status === ContentStageCandidateStatus.Completed;
  useEffect(() => {
    if (isAlreadyCompleted) {
      navigate(backUrl, { replace: true });
    }
  }, [isAlreadyCompleted, backUrl, navigate]);

  if (loading || !role || !stage) {
    return <Loader isOverlay />;
  }

  const handleFinishedClick = () => {
    markComplete({
      variables: { contentStageCandidateId: stage.contentStageCandidateId },
    });
  };

  const branding = role.company.branding;

  return (
    <>
      <HelmetAndPageAnnouncer pageTitle={stage.name} />
      <div
        className="RoleStageContent"
        style={getPortalBrandingStyle(branding)}
        aria-live="polite"
      >
        <PortalNavbar
          companyName={role.company.name}
          logoUrl={branding?.logoUrl || role.company.logoUrl}
        />
        <nav className="RoleStageContent__Nav">
          <NavLink
            to={backUrl}
            onClick={e => {
              e.preventDefault();
              navigate(backUrl);
            }}
            aria-label={t('roles.backToStageList')}
            direction="backward"
            className="RoleStageContent__Nav__BackButton"
          >
            {t('roles.stageList')}
          </NavLink>
        </nav>
        {stage.imageUrl && (
          <div className="RoleStageContent__Hero">
            <img src={stage.imageUrl} alt="" />
          </div>
        )}
        <main className="RoleStageContent__Main">
          <h1 className="RoleStageContent__Title">{stage.name}</h1>
          <p className="RoleStageContent__Description">{stage.description}</p>
          <div className="RoleStageContent__Blocks">
            {stage.blocks.map((block, index) => (
              <ContentBlock key={index} block={block} />
            ))}
          </div>
          <div className="RoleStageContent__Footer">
            <Button
              onClick={handleFinishedClick}
              loading={isCompleting}
              className="RoleStageContent__FinishedButton"
              data-testid="content-stage-finished-button"
            >
              {t('contentStage.finished')}
            </Button>
          </div>
        </main>
        <PortalFooter
          companyName={role.company.name}
          websiteLinkUrl={branding?.websiteLinkUrl}
          careersLinkUrl={branding?.careersLinkUrl}
        />
      </div>
    </>
  );
};

export default RoleStageContent;
